import { forwardRef } from "react";
import clsx from "clsx";
import dayjs from "configs/dayjs";
import { PurchaseOrderResponseType } from "constants/types/purchaseOrder";
import s from "./PurchaseOrderPrintTemplate.module.css";

const fmtNumber = (n: number) => n.toLocaleString("vi-VN");
const fmtDate = (ts: number) => dayjs(ts * 1000).format("DD-MM-YYYY");

interface Props {
  purchaseOrder: PurchaseOrderResponseType;
}

const PurchaseOrderPrintTemplate = forwardRef<HTMLDivElement, Props>(
  ({ purchaseOrder }, ref) => {
    const { partner, warehouse, createdBy, items = [], notes, code, totalAmount, createdAt } =
      purchaseOrder;

    return (
      <div ref={ref} className={s.page}>
        {/* ── Title ─────────────────────────────────────────────────────────── */}
        <div className={s.titleSection}>
          <div className={s.title}>PHIẾU MUA HÀNG</div>
        </div>
        <div className={s.date}>Ngày: {fmtDate(createdAt)}</div>

        {/* ── Partner info table ────────────────────────────────────────────── */}
        <table className={s.infoTable}>
          <tbody>
            <tr>
              <td className={clsx(s.infoCell, s.infoLabelCell)} rowSpan={2}>
                Công ty
              </td>
              <td className={clsx(s.infoCell, s.infoValueCell)} rowSpan={2}>
                {partner.name}
              </td>
              <td className={clsx(s.infoCell, s.infoRightLabelCell)}>Điện thoại</td>
              <td className={clsx(s.infoCell, s.infoRightValueCell)}>{partner.phone}</td>
            </tr>
            <tr>
              <td className={clsx(s.infoCell, s.infoRightLabelCell)}>Mã số thuế</td>
              <td className={clsx(s.infoCell, s.infoRightValueCell)}>{partner.taxCode}</td>
            </tr>
            <tr>
              <td className={clsx(s.infoCell, s.infoLabelCell)}>Địa chỉ</td>
              <td className={clsx(s.infoCell, s.infoValueCell)}>{partner.notes ?? ""}</td>
              <td className={clsx(s.infoCell, s.infoRightLabelCell)}>Số hóa đơn</td>
              <td className={clsx(s.infoCell, s.infoRightValueCell)}>{code}</td>
            </tr>
          </tbody>
        </table>

        {/* ── Supply note ───────────────────────────────────────────────────── */}
        <div className={s.supplyNote}>
          Đã cung cấp cho <strong>{warehouse.name}</strong> các mặt hàng như sau:
        </div>

        {/* ── Items table ───────────────────────────────────────────────────── */}
        <table className={s.table}>
          <thead>
            <tr className={s.tableHeaderRow}>
              <th className={clsx(s.th, s.colCode)}>Mã mặt hàng</th>
              <th className={s.th}>Tên mặt hàng [tên thông số]</th>
              <th className={clsx(s.th, s.colQty)}>Số lượng</th>
              <th className={clsx(s.th, s.colPrice)}>Đơn giá</th>
              <th className={clsx(s.th, s.colTotal)}>Số tiền trước thuế</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className={clsx(s.td, s.colCode)}>{item.product?.code ?? ""}</td>
                <td className={s.td}>
                  {item.productName}
                  {item.productSpec && (
                    <span className={s.productSpec}> [{item.productSpec}]</span>
                  )}
                </td>
                <td className={clsx(s.td, s.colQty)}>{fmtNumber(item.quantity)}</td>
                <td className={clsx(s.td, s.colPrice)}>{fmtNumber(item.unitPrice)}</td>
                <td className={clsx(s.td, s.colTotal)}>{fmtNumber(item.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Footer: delivery note + totals ────────────────────────────────── */}
        <div className={s.footerSection}>
          <div className={s.deliveryNote}>
            <strong>{partner.name}</strong> đã bàn giao hàng đầy đủ.
          </div>

          <table className={s.totalsTable}>
            <tbody>
              <tr>
                <td className={s.totalLabelCell}>Tổng tiền hàng</td>
                <td className={s.totalValueCell}>{fmtNumber(totalAmount)}</td>
              </tr>
              <tr>
                <td className={s.totalLabelCell}>Tổng thuế GTGT</td>
                <td className={s.totalValueCell}></td>
              </tr>
              <tr>
                <td className={clsx(s.totalLabelCell, s.totalLabelCellBold)}>Tổng tiền</td>
                <td className={clsx(s.totalValueCell, s.totalValueCellBold)}>
                  {fmtNumber(totalAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Notes ─────────────────────────────────────────────────────────── */}
        {notes && (
          <div className={s.notesSection}>
            <div className={s.notesTitle}>Ghi chú</div>
            <div className={s.notesText}>{notes}</div>
          </div>
        )}

        {/* ── Signatures ────────────────────────────────────────────────────── */}
        <div className={s.signatureSection}>
          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Nhà cung cấp</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
          </div>

          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Người nhận hàng</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
          </div>

          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Thủ kho ({warehouse.name})</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
          </div>

          <div className={s.signatureCol}>
            <div className={s.signatureTitle}>Người lập</div>
            <span className={s.signatureSubtitle}>Ký, họ tên</span>
            <span className={s.signatureName}>{createdBy.fullname}</span>
          </div>
        </div>
      </div>
    );
  },
);

PurchaseOrderPrintTemplate.displayName = "PurchaseOrderPrintTemplate";

export default PurchaseOrderPrintTemplate;
